import {ioGetDocuments} from "@/febos/servicios/api/customDocuments.api";
import Vue from "vue";
export default {
  data() {
    const { type } = this.$route.params;
    return {
      type,
      createdDocuments: [],
      previousDocument: {},
      documentsPage: 1,
      hasMoreDocuments: false,
      loadingMoreDocuments: false,
      dfsResetKey: 0,
    };
  },
  methods: {
    resetDfsState() {
      this.attachments = [];
      this.dfsFiles = [];
      if (this.dynamicSelectorFiles !== undefined) {
        this.dynamicSelectorFiles = [];
      }
      this.dfsResetKey += 1;
    },
    async loadDocumentsPage() {
      const PAGE_SIZE = 100;
      const { data } = await ioGetDocuments(this.type, {
        elementosPorPagina: PAGE_SIZE,
        pagina: this.documentsPage,
      });
      const filtered = data.documents
        .filter((file) => file.createdBy === this.usr.id && file.status !== '3')
        .map((item) => ({ ...item, id: item.id, value: item.id, label: item.name }));
      this.createdDocuments = [...this.createdDocuments, ...filtered];
      this.hasMoreDocuments = this.createdDocuments.length < data.totalElementos;
    },
    async loadMoreDocuments() {
      if (this.loadingMoreDocuments || !this.hasMoreDocuments) return;
      this.loadingMoreDocuments = true;
      try {
        this.documentsPage += 1;
        await this.loadDocumentsPage();
      } finally {
        this.loadingMoreDocuments = false;
      }
    },
    getDocumentData(file) {
      if (!file) {
        // Limpiar adjuntos y DFS al deseleccionar el documento
        this.resetDfsState();
        return;
      }

      const fieldsToExclude = [
        'status',
        'numero',
        'createdBy',
        'createdName',
        'createdAt',
        'trackingId',
        'locationId',
        'locationType',
        'id',
        'value',
        'label',
      ];
      const filteredData = Object.keys(file).reduce((acc, key) => {
        if (!fieldsToExclude.includes(key)) {
          acc[key] = file[key];
        }
        return acc;
      }, {});

      // Filtrar attachments incompletos y deduplicar por id
      const seen = new Set();
      const attachments = (filteredData.attachments || []).filter((f) => {
        if (!f.id || !f.name) return false;
        if (seen.has(f.id)) return false;
        seen.add(f.id);
        return true;
      });

      // Asignar origen a archivos que no lo tienen, usando el primer selector del DFS
      if (this.dfsSelectors && this.dfsSelectors.length) {
        const defaultOrigen = this.dfsSelectors[0].value
          || this.dfsSelectors[0].extra?.value
          || this.dfsSelectors[0].label;
        attachments.forEach((f) => {
          if (!f.origen) {
            f.origen = defaultOrigen;
          }
        });
      }

      // Marcar archivos como provenientes del repositorio (documento previo)
      attachments.forEach((f) => {
        if (!f.source) {
          f.source = 'repositorio';
        }
      });

      // Preservar campos del borrador actual si estamos editando un draft
      const preservedFields = {};
      if (this.$route.query.from === 'draft' && this.formData) {
        fieldsToExclude.forEach((key) => {
          if (this.formData[key] !== undefined) {
            preservedFields[key] = this.formData[key];
          }
        });
      }

      // Limpiar adjuntos previos antes de asignar los del nuevo documento
      this.resetDfsState();

      // Asignar formData con attachments deduplicados y campos preservados del borrador
      this.formData = { ...filteredData, ...preservedFields, status: preservedFields.status || '1', attachments };

      // Mapear attachments al DFS (sin archivos manuales tipo adjunto)
      this.dfsFiles = attachments.filter((f) => f.origen !== 'adjunto');
      if (this.dynamicSelectorFiles !== undefined) {
        this.dynamicSelectorFiles = attachments.filter((f) => f.origen !== 'adjunto');
      }
      this.attachments = attachments;

      // Mapeo específico por tipo de documento
      if (this.type === 'ACT') {
        // Resolver archivos CPSH del repositorio con 3 fuentes posibles:
        // 1. cpshFilesSelected (existe en borradores)
        // 2. Archivos con nombre "CPSH" en attachments (documentos nuevos)
        // 3. Construir desde el documento original (documentos antiguos sin cpshFilesSelected)
        let cpshFiles = filteredData.cpshFilesSelected;
        if (!cpshFiles || !cpshFiles.length) {
          cpshFiles = attachments.filter((f) => f.name && f.name.includes('CPSH'));
        }
        if (!cpshFiles || !cpshFiles.length) {
          // Documento antiguo: construir entrada CPSH desde el objeto original
          if (file.name && file.name.includes('CPSH')) {
            const fecha = file.createdAt
              ? Vue.moment(file.createdAt).format('DD-MM-YYYY HH:mm:ss')
              : '';
            cpshFiles = [{
              id: file.id,
              archivoId: file.id,
              name: file.name || `CPSH - N° ${file.numero || ''} - ${fecha}.pdf`,
              currentDescription: file.name || `CPSH - N° ${file.numero || ''} - ${fecha}.pdf`,
              source: 'repositorio',
              origen: 'repositorio',
              tipo: 'repositorio',
              mime: 'application/pdf',
              date: file.createdAt || '',
            }];
          } else if (file.linked && file.linked.length) {
            // Fallback: usar linked para obtener el id del CPSH
            cpshFiles = [{
              id: file.linked[0].id,
              archivoId: file.linked[0].id,
              source: 'repositorio',
              origen: 'repositorio',
              tipo: 'repositorio',
            }];
          }
        }
        // Asegurar que todos los archivos CPSH tengan source: 'repositorio'
        cpshFiles = (cpshFiles || []).map((f) => ({
          ...f,
          source: f.source || 'repositorio',
          origen: f.origen || 'repositorio',
        }));
        this.attachments = [...cpshFiles];
        this.isDocenteForm = filteredData.formType === 'Docente';
        // Cargar datos del primer CPSH
        if (this.attachments.length) {
          this.getSelectedFileData(this.attachments[0]);
        }
        // Excluir CPSH files del DFS
        const cpshIds = new Set(cpshFiles.map((f) => f.id));
        this.dfsFiles = attachments.filter((f) => f.origen !== 'adjunto' && !cpshIds.has(f.id));
        if (this.dynamicSelectorFiles !== undefined) {
          this.dynamicSelectorFiles = attachments.filter((f) => f.origen !== 'adjunto' && !cpshIds.has(f.id));
        }
      }

      if (this.type === 'GRC' || this.type === 'FCA') {
        // Resolver archivos FISABS del repositorio con 3 fuentes posibles:
        // 1. fisabsFilesSelected (existe en borradores)
        // 2. Archivos con nombre "FISABS" en attachments (documentos nuevos)
        // 3. Construir desde selectedFisabs (documentos antiguos sin attachments FISABS)
        let fisabsFiles = filteredData.fisabsFilesSelected;
        if (!fisabsFiles || !fisabsFiles.length) {
          fisabsFiles = attachments.filter((f) => f.name && f.name.includes('FISABS'));
        }
        if (!fisabsFiles || !fisabsFiles.length) {
          // Documentos antiguos: construir desde selectedFisabs
          fisabsFiles = (filteredData.selectedFisabs || []).map((sf) => {
            const fecha = sf.createdAt
              ? Vue.moment(sf.createdAt).format('DD-MM-YYYY HH:mm:ss')
              : '';
            return {
              id: sf.id,
              archivoId: sf.id,
              name: `FISABS - N° ${sf.numero || ''} - ${fecha}.pdf`,
              currentDescription: `FISABS - N° ${sf.numero || ''} - ${fecha}.pdf`,
              source: 'repositorio',
              origen: 'repositorio',
              tipo: 'repositorio',
              mime: 'application/pdf',
              date: sf.createdAt || '',
            };
          });
        }
        // Asegurar que todos los archivos FISABS tengan source: 'repositorio'
        fisabsFiles = fisabsFiles.map((f) => ({
          ...f,
          source: f.source || 'repositorio',
          origen: f.origen || 'repositorio',
        }));
        this.attachments = [...fisabsFiles];
        this.selectedDocument = fisabsFiles.find((f) => f.name && f.name.includes('FISABS'))
          || fisabsFiles[0]
          || {};
        // Excluir FISABS del DFS
        const fisabsIds = new Set(fisabsFiles.map((f) => f.id));
        this.dfsFiles = attachments.filter((f) => f.origen !== 'adjunto' && !fisabsIds.has(f.id));
        if (this.dynamicSelectorFiles !== undefined) {
          this.dynamicSelectorFiles = attachments.filter((f) => f.origen !== 'adjunto' && !fisabsIds.has(f.id));
        }
        // Llamar getSelectedFileData para cargar items del FISABS si no hay items cargados
        if (fisabsFiles.length && this.getSelectedFileData && (!this.formData.items || !this.formData.items.length)) {
          this.getSelectedFileData(fisabsFiles[0]);
        }
      }

      if (this.type === 'SCH') {
        this.experienceFiles = attachments.filter((f) => f.origen === 'experiencia');
        this.expertiseFiles = attachments.filter((f) => f.origen === 'expertise');
        const schExcludedOrigins = ['experiencia', 'expertise', 'adjunto'];
        this.dynamicSelectorFiles = attachments
          .filter((f) => !schExcludedOrigins.includes(f.origen));
      }

      if (this.type === 'FISABS') {
        // Excluir el FISABS vinculado ("anterior asociado") del DFS
        const linkedFisabsId = filteredData.fisabsSelected
          || (filteredData.createdFisabsSelected && filteredData.createdFisabsSelected.id)
          || '';
        if (linkedFisabsId) {
          this.dfsFiles = this.dfsFiles.filter((f) => f.id !== linkedFisabsId);
          if (this.dynamicSelectorFiles !== undefined) {
            this.dynamicSelectorFiles = this.dynamicSelectorFiles.filter((f) => f.id !== linkedFisabsId);
          }
        }
      }

      // Normalización de fechas de periodicidad para FISABS
      if (this.type === 'FISABS' && this.formData.items) {
        this.formData.items.forEach((item) => {
          if (item.serviceDeliveryPeriodicity && item.serviceDeliveryPeriodicity.length > 0) {
            const normalizedDates = [];
            const normalize = (f) => {
              const partes = f.trim().split('-');
              if (partes[0].length === 4) {
                return `${partes[0]}-${partes[1].padStart(2, '0')}-${partes[2].padStart(2, '0')}`;
              }
              return `${partes[2]}-${partes[1].padStart(2, '0')}-${partes[0].padStart(2, '0')}`;
            };
            item.serviceDeliveryPeriodicity.forEach((date) => {
              if (date.includes('hasta')) {
                const [start, end] = date.split('hasta');
                normalizedDates.push(normalize(start));
                normalizedDates.push(normalize(end));
              } else {
                normalizedDates.push(normalize(date));
              }
            });
            this.$set(item, 'serviceDeliveryPeriodicity', normalizedDates);
          }
        });
      }

      // Forzar re-render del formulario
      this.draftKey += 1;
    },
  },
  async created() {
    this.usr = this.$store.getters['Usuario/currentUser'];
    await this.loadDocumentsPage();
  },
}
